import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link2, RefreshCw, AlertTriangle, UserPlus, Mail, Users } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  useAdminConnections,
  useAdminInviteStats,
  ConnectionsTimeframe,
} from '../api/useAdminConnections';
import { useAdminNoShows } from '../api/useAdminNoShows';
import { Loader } from '@/components/Loader';

const TIMEFRAME_OPTIONS: { value: ConnectionsTimeframe; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 180 days' },
  { value: '365d', label: 'Last 365 days' },
  { value: 'all', label: 'All time' },
];

export default function AdminConnections() {
  const [timeframe, setTimeframe] = useState<ConnectionsTimeframe>('30d');
  const { data, isLoading, refetch } = useAdminConnections({ timeframe });
  const { data: inviteStatsData, isLoading: inviteStatsLoading } =
    useAdminInviteStats({ timeframe });
  const { data: noShowData, isLoading: noShowsLoading } = useAdminNoShows();

  const stats = data?.data;
  const perDay = stats?.perDay ?? [];
  const invites = inviteStatsData?.data;
  // Merge sent + accepted per-day rows into a single series keyed by date so
  // the chart renders both totals as side-by-side bars per day.
  const invitePerDay = (() => {
    if (!invites) return [];
    const map = new Map<
      string,
      { date: string; sent: number; accepted: number }
    >();
    for (const row of invites.sentPerDay) {
      const entry = map.get(row.date) || { date: row.date, sent: 0, accepted: 0 };
      entry.sent += row.email + row.connection;
      map.set(row.date, entry);
    }
    for (const row of invites.acceptedPerDay) {
      const entry = map.get(row.date) || { date: row.date, sent: 0, accepted: 0 };
      entry.accepted += row.email + row.connection;
      map.set(row.date, entry);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  })();
  const acceptanceRate = invites && invites.total > 0
    ? Math.round((invites.accepted / invites.total) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Connections</h1>
            <p className="text-dark-muted text-lg">
              User-to-user connections made after dinners
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as ConnectionsTimeframe)}
              className="bg-dark-border text-white px-4 py-2.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none"
            >
              {TIMEFRAME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="btn btn-ghost"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Stat Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {isLoading ? '—' : (stats?.total?.toLocaleString() ?? 0)}
              </p>
              <p className="text-dark-muted text-sm">Total Connections</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {isLoading ? '—' : (stats?.totalMutual?.toLocaleString() ?? 0)}
              </p>
              <p className="text-dark-muted text-sm">Mutual</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {isLoading ? '—' : ((stats?.total ?? 0) - (stats?.totalMutual ?? 0)).toLocaleString()}
              </p>
              <p className="text-dark-muted text-sm">Non-Mutual</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Chart */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      ) : perDay.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-xl font-bold mb-4">Connections per Day</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  tickFormatter={(v) => {
                    const d = new Date(v + 'T00:00:00');
                    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB',
                  }}
                  labelFormatter={(label) => {
                    const d = new Date(label + 'T00:00:00');
                    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                  }}
                />
                <Legend />
                <Bar dataKey="mutual" stackId="connections" name="Mutual" fill="#10B981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="nonMutual" stackId="connections" name="Non-Mutual" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <Link2 className="w-16 h-16 text-dark-muted mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">No Connections Yet</h3>
          <p className="text-dark-muted">
            No connections have been made in this time period.
          </p>
        </motion.div>
      )}

      {/* +1 Invites — sent/accepted breakdown by source (email vs in-app
          connection picker). Uses the same timeframe filter as Connections. */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="mt-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-primary-500" />
          </div>
          <h2 className="text-xl font-bold">+1 Invites</h2>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-primary-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inviteStatsLoading ? '—' : (invites?.total ?? 0).toLocaleString()}
                </p>
                <p className="text-dark-muted text-sm">Total sent</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-dark-border/40 text-xs">
              <div className="flex items-center gap-1.5 text-dark-muted">
                <Mail className="w-3 h-3" />
                <span className="font-medium text-white">
                  {inviteStatsLoading ? '—' : (invites?.byEmail ?? 0).toLocaleString()}
                </span>
                email
              </div>
              <div className="flex items-center gap-1.5 text-dark-muted">
                <Users className="w-3 h-3" />
                <span className="font-medium text-white">
                  {inviteStatsLoading ? '—' : (invites?.byConnection ?? 0).toLocaleString()}
                </span>
                connection
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inviteStatsLoading ? '—' : (invites?.accepted ?? 0).toLocaleString()}
                </p>
                <p className="text-dark-muted text-sm">Accepted</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-dark-border/40 text-xs">
              <div className="flex items-center gap-1.5 text-dark-muted">
                <Mail className="w-3 h-3" />
                <span className="font-medium text-white">
                  {inviteStatsLoading ? '—' : (invites?.acceptedByEmail ?? 0).toLocaleString()}
                </span>
                email
              </div>
              <div className="flex items-center gap-1.5 text-dark-muted">
                <Users className="w-3 h-3" />
                <span className="font-medium text-white">
                  {inviteStatsLoading ? '—' : (invites?.acceptedByConnection ?? 0).toLocaleString()}
                </span>
                connection
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inviteStatsLoading ? '—' : (invites?.pending ?? 0).toLocaleString()}
                </p>
                <p className="text-dark-muted text-sm">Pending</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-dark-border/40 text-xs text-dark-muted">
              <span>
                <span className="font-medium text-white">
                  {inviteStatsLoading ? '—' : (invites?.cancelled ?? 0).toLocaleString()}
                </span>{' '}
                cancelled
              </span>
              <span>
                <span className="font-medium text-white">
                  {inviteStatsLoading ? '—' : (invites?.expired ?? 0).toLocaleString()}
                </span>{' '}
                expired
              </span>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <span className="text-primary-500 font-bold text-sm">%</span>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inviteStatsLoading ? '—' : `${acceptanceRate}%`}
                </p>
                <p className="text-dark-muted text-sm">Acceptance rate</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-dark-border/40 text-xs text-dark-muted">
              Accepted ÷ total sent in window
            </div>
          </div>
        </div>

        {/* Sent vs Accepted per-day chart */}
        {inviteStatsLoading ? (
          <div className="card flex items-center justify-center py-12">
            <Loader />
          </div>
        ) : invitePerDay.length > 0 ? (
          <div className="card">
            <h3 className="text-lg font-bold mb-4">Sent vs Accepted per Day</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={invitePerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    stroke="#9CA3AF"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickFormatter={(v) => {
                      const d = new Date(v + 'T00:00:00');
                      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '0.5rem',
                      color: '#F9FAFB',
                    }}
                    labelFormatter={(label) => {
                      const d = new Date(label + 'T00:00:00');
                      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sent"
                    name="Sent"
                    stroke="#6366F1"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#6366F1' }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="accepted"
                    name="Accepted"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#10B981' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="card text-center py-12">
            <UserPlus className="w-12 h-12 text-dark-muted mx-auto mb-3" />
            <h3 className="text-base font-bold mb-1">No invites in this window</h3>
            <p className="text-dark-muted text-sm">
              Nothing to chart for the selected timeframe.
            </p>
          </div>
        )}
      </motion.div>

      {/* No-Show Reports */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card mt-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <h2 className="text-xl font-bold">No-Show Reports by User</h2>
        </div>

        {noShowsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        ) : (noShowData?.data?.length ?? 0) > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-dark-border text-dark-muted text-sm">
                  <th className="pb-3 pr-4">User</th>
                  <th className="pb-3 px-4 text-right">Total Reports</th>
                  <th className="pb-3 pl-4 text-right">Dinners w/ 2+ Reports</th>
                </tr>
              </thead>
              <tbody>
                {noShowData!.data!.map((row) => (
                  <tr key={row.userId} className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors">
                    <td className="py-3 pr-4 font-medium">
                      <a href={`/admin/users/?q=${row.userId}`} className="hover:text-primary-400 transition-colors underline underline-offset-2">
                        {row.firstName} {row.lastName ?? ''}
                      </a>
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">{row.totalReports}</td>
                    <td className="py-3 pl-4 text-right tabular-nums">
                      {row.dinnersWithMultipleReports > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                          {row.dinnersWithMultipleReports}
                        </span>
                      ) : (
                        <span className="text-dark-muted">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-dark-muted text-center py-8">No no-show reports found.</p>
        )}
      </motion.div>
    </div>
  );
}
